import { createStore, storage } from './lib/store';

export type ThemePreference = 'system' | 'light' | 'dark';

/** Speed sheet toggle per book (Flutter PlaybackSpeedScope). */
export type SpeedScope = 'thisBook' | 'allBooks';

export interface EqualizerSettings {
  /** dB, -12..+12. */
  preamp: number;
  /** -10..+10. */
  balance: number;
  /** Five bands, dB, -12..+12 (Low / Bass, Low-Mid, Mid, Upper-Mid, High / Treble). */
  bands: [number, number, number, number, number];
}

/** Metadata screen toggles (shown disabled in Flutter: nothing consumes them yet). */
export interface MetadataSettings {
  preferEmbeddedCover: boolean;
  preferServerMetadata: boolean;
  refreshOnOpen: boolean;
  seriesFirstFormatting: boolean;
}

export interface Settings {
  theme: ThemePreference;
  defaultPlaybackRate: number;
  skipBackSeconds: number;
  skipForwardSeconds: number;
  /** Rewind a little when resuming after a long pause, so you don't lose your place. */
  smartRewind: boolean;
  sleepTimerFadeOut: boolean;
  /** Let the browser pop the player out into its own window when you switch tabs (desktop Chrome / Edge). */
  popOutOnTabSwitch: boolean;
  notifyDownloads: boolean;
  notifySleepTimer: boolean;
  notifyFinished: boolean;
  /** Last library picked on this device (the active connection's selection takes precedence). */
  selectedLibraryId?: string;
  /** Per-book playback rate overrides. */
  bookRates: Record<string, number>;
  /** Which speed-sheet toggle each book uses. */
  bookSpeedScope: Record<string, SpeedScope>;
  /** Speed presets (Flutter stores them; no screen shows them). */
  speedPresets: number[];
  /** Sleep timer presets in minutes, ascending. Empty reads back as the defaults. */
  sleepPresets: number[];
  /** Output level in percent: 100 = unchanged, 0..200. */
  audioBoost: number;
  equalizer: EqualizerSettings;
  metadata: MetadataSettings;
  /** Only `internal` exists on the web (OPFS). */
  storageLocation: 'internal';
}

export const SPEED_MIN = 0.5;
export const SPEED_MAX = 3;
export const SPEED_STEP = 0.05;
export const DEFAULT_SPEED_PRESETS = [0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2];
export const DEFAULT_SLEEP_PRESETS = [10, 15, 30, 45, 60];
export const SLEEP_PRESET_MIN = 1;
export const SLEEP_PRESET_MAX = 1440;
export const AUDIO_BOOST_MIN = 0;
export const AUDIO_BOOST_MAX = 200;
export const DEFAULT_EQUALIZER: EqualizerSettings = { preamp: 0, balance: 0, bands: [0, 0, 0, 0, 0] };

export const DEFAULT_SETTINGS: Settings = {
  // Flutter's ThemeProvider defaults to light.
  theme: 'light',
  defaultPlaybackRate: 1,
  skipBackSeconds: 10,
  skipForwardSeconds: 30,
  smartRewind: true,
  sleepTimerFadeOut: true,
  popOutOnTabSwitch: true,
  notifyDownloads: true,
  notifySleepTimer: true,
  notifyFinished: true,
  bookRates: {},
  bookSpeedScope: {},
  speedPresets: DEFAULT_SPEED_PRESETS,
  sleepPresets: DEFAULT_SLEEP_PRESETS,
  audioBoost: 100,
  equalizer: DEFAULT_EQUALIZER,
  metadata: { preferEmbeddedCover: false, preferServerMetadata: false, refreshOnOpen: false, seriesFirstFormatting: false },
  storageLocation: 'internal',
};

const KEY = 'plume.settings.v1';

export const settingsStore = createStore<Settings>({ ...DEFAULT_SETTINGS, ...storage.read<Partial<Settings>>(KEY, {}) });
settingsStore.subscribe(() => storage.write(KEY, settingsStore.get()));

export function updateSettings(patch: Partial<Settings>): void {
  settingsStore.set((s) => ({ ...s, ...patch }));
}

/** Clamp to 0.5–3.0 and round to 0.05 (Flutter `normalizePlaybackSpeed`). */
export function normalizePlaybackSpeed(speed: number): number {
  const clamped = Math.min(SPEED_MAX, Math.max(SPEED_MIN, Number.isFinite(speed) ? speed : 1));
  return Number((Math.round(clamped / SPEED_STEP) * SPEED_STEP).toFixed(2));
}

/** Sleep presets in effect: the stored list, or the defaults when it is empty (Flutter quirk kept). */
export function getSleepPresets(settings: Pick<Settings, 'sleepPresets'> = settingsStore.get()): number[] {
  const valid = (settings.sleepPresets ?? []).filter((m) => Number.isInteger(m) && m > 0);
  return valid.length > 0 ? [...valid].sort((a, b) => a - b) : [...DEFAULT_SLEEP_PRESETS];
}

/** Adds a preset (1–1440 minutes); duplicates are ignored. */
export function addSleepPreset(minutes: number): void {
  const m = Math.round(Math.min(SLEEP_PRESET_MAX, Math.max(SLEEP_PRESET_MIN, minutes)));
  const current = getSleepPresets();
  if (current.includes(m)) return;
  updateSettings({ sleepPresets: [...current, m].sort((a, b) => a - b) });
}

export function removeSleepPreset(minutes: number): void {
  updateSettings({ sleepPresets: getSleepPresets().filter((m) => m !== minutes) });
}

/** Stores the speed-sheet scope for a book. */
export function setBookSpeedScope(bookId: string, scope: SpeedScope): void {
  settingsStore.set((s) => (s.bookSpeedScope[bookId] === scope ? s : { ...s, bookSpeedScope: { ...s.bookSpeedScope, [bookId]: scope } }));
}

/** Scope a book uses: its saved toggle, else `thisBook` when it has an override, else `allBooks`. */
export function getBookSpeedScope(bookId: string, settings: Settings = settingsStore.get()): SpeedScope {
  return settings.bookSpeedScope[bookId] ?? (settings.bookRates[bookId] != null ? 'thisBook' : 'allBooks');
}

export function setAudioBoost(percent: number): void {
  updateSettings({ audioBoost: Math.round(Math.min(AUDIO_BOOST_MAX, Math.max(AUDIO_BOOST_MIN, percent))) });
}

export function setEqualizer(patch: Partial<EqualizerSettings>): void {
  settingsStore.set((s) => ({ ...s, equalizer: { ...s.equalizer, ...patch } }));
}

export function resetEqualizer(): void {
  updateSettings({ equalizer: { ...DEFAULT_EQUALIZER, bands: [0, 0, 0, 0, 0] } });
}
